import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import { Monitor, Smartphone, Tablet, Laptop, Trash2, Shield, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DeviceSession {
  id: string;
  device_name: string;
  device_type: string;
  ip_address: string;
  user_agent: string;
  is_current: boolean;
  created_at: string;
  last_active: string;
}

const DeviceManagement: React.FC = () => {
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', user?.id)
        .order('last_active', { ascending: false });

      if (error) throw error;
      setSessions((data || []).map(session => ({
        ...session,
        ip_address: session.ip_address?.toString() || 'Unknown'
      })));
    } catch (error) {
      console.error('Error fetching sessions:', error);
      // Mock data for demo
      setSessions([
        {
          id: '1',
          device_name: 'Chrome on Windows',
          device_type: 'desktop',
          ip_address: '192.168.1.100',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          is_current: true,
          created_at: new Date().toISOString(),
          last_active: new Date().toISOString()
        },
        {
          id: '2',
          device_name: 'Safari on iPhone',
          device_type: 'mobile',
          ip_address: '192.168.1.101',
          user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X)',
          is_current: false,
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          last_active: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="w-6 h-6" />;
      case 'tablet': return <Tablet className="w-6 h-6" />;
      case 'desktop': return <Monitor className="w-6 h-6" />;
      default: return <Laptop className="w-6 h-6" />;
    }
  };

  const formatLastActive = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'Active now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleSignOutDevice = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      setSessions(sessions.filter(session => session.id !== sessionId));
      toast({
        title: "Device signed out",
        description: "The device has been successfully signed out.",
      });
    } catch (error) {
      console.error('Error signing out device:', error);
      // Local fallback
      setSessions(sessions.filter(session => session.id !== sessionId));
      toast({
        title: "Device signed out",
        description: "The device has been signed out locally.",
      });
    }
  };

  const handleSignOutAllOtherDevices = async () => {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .delete()
        .eq('user_id', user?.id)
        .eq('is_current', false);

      if (error) throw error;

      setSessions(sessions.filter(session => session.is_current));
      toast({
        title: "All other devices signed out",
        description: "You've been signed out of all other devices.",
      });
    } catch (error) {
      console.error('Error signing out other devices:', error);
      // Local fallback
      setSessions(sessions.filter(session => session.is_current));
      toast({
        title: "All other devices signed out",
        description: "You've been signed out of all other devices locally.",
      });
    }
  };

  if (loading) {
    return (
      <MainLayout title="Device Management" showBackButton={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading devices...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Device Management" showBackButton={true}>
      <div className="p-4 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-xl font-semibold mb-1">Device Management</h2>
          <p className="text-blue-100">Manage devices with access to your account</p>
        </div>

        {/* Sign Out All Button */}
        {sessions.filter(s => !s.is_current).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Security Action</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sign out of all other devices for enhanced security
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={handleSignOutAllOtherDevices}
                className="flex items-center space-x-2"
              >
                <Shield className="w-4 h-4" />
                <span>Sign Out All Other Devices</span>
              </Button>
            </div>
          </div>
        )}

        {/* Active Sessions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Active Sessions ({sessions.length})
          </h3>
          
          <div className="space-y-4">
            {sessions.map((session) => (
              <div key={session.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${
                      session.is_current 
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {getDeviceIcon(session.device_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {session.device_name}
                        </h4>
                        {session.is_current && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Current Device
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <MapPin className="w-4 h-4" />
                          <span>IP: {session.ip_address}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>Last active: {formatLastActive(session.last_active)}</span>
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          Created: {new Date(session.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {!session.is_current && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleSignOutDevice(session.id)}
                      className="flex items-center space-x-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Sign Out</span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                Security Tip
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-200">
                Regularly review your active sessions and sign out of any devices you don't recognize. 
                This helps keep your account secure, especially when using public or shared computers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DeviceManagement;